import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setDrawer, setDrawerAlert, setStage } from 'slices/drawer'
import {
  ACTIVATION_PENDING_STAGES,
  DEFAULT_ERROR_MESSAGE,
  FIREBASE_EVENTS,
  SCHEDULE_TYPE,
  SCHEDULING_SHIFT_LABEL,
  SERVICE_ORDER_TYPES,
} from 'app-constants'
import {
  Button,
  Input,
  CustomDatePicker,
  ContextLoader,
  RadioButton,
  Card,
  SelectInput,
} from 'components'
import { useFirebaseEvent } from 'hooks'
import { convertISOStringToDate, formatDate, formatTurn, formatZipCode, toISO } from 'utils'
import { bookingService } from 'services'
import './style.scss'

export function ReschedulingServiceOrder({ setBookingSelected, serviceOrder }) {
  const { expectedInstallationDate, expectedInstallationTurn, osId: serviceOrderId } = serviceOrder
  const { unity, id: contractId, address } = useSelector(state => state.contract.selected)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState(null)
  const [bookingAvailableDays, setBookingAvailableDays] = useState([])
  const [availableShifts, setAvailableShifts] = useState([])
  const [selectedShift, setSelectedShift] = useState(null)
  const [bookingCode, setBookingCode] = useState(null)
  const [reference, setReference] = useState('')
  const [selectedTimeFrames, setSelectedTimeFrames] = useState({})
  const [bucket, setBucket] = useState('')
  const [workzone, setWorkzone] = useState('')
  const [firstAvailableDate, setFirstAvailableDate] = useState(null)
  const [scheduleId, setScheduleId] = useState(null)
  const dispatch = useDispatch()
  const { city, neighborhood, number, street, uf, zipCode } = address
  const { sendEvent } = useFirebaseEvent()
  const { getBookingsActivation } = bookingService()

  useEffect(() => {
    async function fetchData() {
      try {
        const params = {
          unity,
          contractId,
          number,
          city,
          district: neighborhood,
          street,
          state: uf,
          zipCode: formatZipCode(zipCode),
          serviceOrderType: SERVICE_ORDER_TYPES.ACTIVATION,
        }

        const { data } = await getBookingsActivation({ params })

        setBookingAvailableDays(data?.bookingDaysAvailable || [])
        setBookingCode(data?.code)
        setFirstAvailableDate(data?.firstAvailableDate)
        setWorkzone(data?.workZoneCode)
        setScheduleId(data?.scheduleId)
      } catch (error) {
        const errorMessage = error?.response?.data?.message || DEFAULT_ERROR_MESSAGE
        console.error(error)
        dispatch(setDrawerAlert({ severity: 'error', title: errorMessage }))
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [contractId, serviceOrderId, unity, city, neighborhood, number, street, uf, zipCode, dispatch])

  function handleContinue() {
    setBookingSelected({
      date: selectedDate,
      turn: selectedShift,
      reference,
      bookingCode,
      serviceOrderId,
      contractId,
      timeFrames: selectedTimeFrames,
      bucket,
      firstAvailableDate,
      workzone,
      scheduleId,
    })

    dispatch(setStage(ACTIVATION_PENDING_STAGES.RESCHEDULE_ACTIVATION_CONFIRMATION))
  }

  function handleCancel() {
    dispatch(setDrawer(''))
  }

  function mapBookingAvailableDays() {
    return bookingAvailableDays.map(({ date }) => convertISOStringToDate(date))
  }

  const getScheduleData = useCallback(
    date => bookingAvailableDays.find(day => day?.date === toISO(date)),
    [bookingAvailableDays]
  )

  const handleChangeSchedule = useCallback(
    date => {
      sendEvent(FIREBASE_EVENTS.SERVICE_ORDER_RESCHEDULE_SELECT_DATE)
      setSelectedDate(date)

      const selectedDateScheduleData = getScheduleData(date)

      const defaultShift = selectedDateScheduleData?.bookingShiftAvailabilities[0]

      setSelectedShift(defaultShift?.shift)
      setSelectedTimeFrames(defaultShift || {})
      setAvailableShifts(selectedDateScheduleData?.bookingShiftAvailabilities)
      setBucket(selectedDateScheduleData?.bucket)
    },
    [getScheduleData, sendEvent]
  )

  function getDisableButton() {
    return (
      !selectedDate ||
      !selectedShift ||
      !selectedTimeFrames?.initialTimeFrame ||
      !selectedTimeFrames?.finalTimeFrame
    )
  }

  function getScheduleType() {
    return SCHEDULE_TYPE.TURN
  }

  function getSelectedTimeFramePosition(timeFrameList) {
    return timeFrameList.findIndex(
      ({ initialTimeFrame, finalTimeFrame }) =>
        initialTimeFrame === selectedTimeFrames?.initialTimeFrame &&
        finalTimeFrame === selectedTimeFrames?.finalTimeFrame
    )
  }

  function renderButton() {
    return (
      <div className='links'>
        <Button
          className='button'
          color='primary'
          size='large'
          isLoading={isLoading}
          disabled={getDisableButton()}
          onClick={() => {
            handleContinue()
          }}
        >
          Continuar
        </Button>
        <Button
          className='button'
          color='primary'
          size='large'
          outlined
          onClick={() => {
            handleCancel()
          }}
        >
          Cancelar
        </Button>
      </div>
    )
  }

  function renderDefaultTurn() {
    return availableShifts.map(({ shift, initialTimeFrame, finalTimeFrame }) => (
      <Card
        className={`card ${selectedShift === shift && '-selected'}`}
        onClick={() => {
          setSelectedShift(shift)
          setSelectedTimeFrames({ initialTimeFrame, finalTimeFrame })
        }}
        key={shift}
      >
        <RadioButton id={shift} checked={selectedShift === shift} />
        <p className='text-card'>
          {SCHEDULING_SHIFT_LABEL[shift]} ({initialTimeFrame} - {finalTimeFrame})
        </p>
      </Card>
    ))
  }

  function renderTurns() {
    return availableShifts.map(({ shift }) => (
      <Card
        className={`card ${selectedShift === shift && '-selected'}`}
        onClick={() => {
          setSelectedTimeFrames({})
          setSelectedShift(shift)
        }}
        key={shift}
      >
        <RadioButton id={shift} checked={selectedShift === shift} />
        <p className='text-card'>{SCHEDULING_SHIFT_LABEL[shift]}</p>
      </Card>
    ))
  }

  function renderTimeFrames() {
    const { bookingShiftTimeFrameAvailabilityResponseDtos: timeFrameList } = availableShifts.find(
      ({ shift }) => shift === selectedShift
    )
    return (
      selectedShift && (
        <>
          <p className='text time-frame'>Escolha o horário de instalação</p>
          <SelectInput
            onChange={({ target: { value: index } }) => setSelectedTimeFrames(timeFrameList[index])}
            className='time-frame-select'
            value={getSelectedTimeFramePosition(timeFrameList)}
          >
            <option value=''>Horário da Instalação</option>
            {timeFrameList?.map((timeFrames, index) => {
              const { initialTimeFrame, finalTimeFrame } = timeFrames
              const name = `${initialTimeFrame} - ${finalTimeFrame}`

              return (
                <option key={name} value={index}>
                  {name}
                </option>
              )
            })}
          </SelectInput>
        </>
      )
    )
  }

  function renderTimeFramesShift() {
    return (
      <>
        {renderTurns()}
        {renderTimeFrames()}
      </>
    )
  }

  // TODO: Gabriel Hoffmann 10/11/2022 - https://dev.azure.com/Sumicity/Sumicity/_workitems/edit/9300
  function renderScheduleShifts() {
    const scheduleType = getScheduleType()

    const SCHEDULE_SHIFTS = {
      [SCHEDULE_TYPE.TURN]: renderDefaultTurn,
      [SCHEDULE_TYPE.TIME_FRAME]: renderTimeFramesShift,
    }

    return (
      availableShifts.length > 0 && (
        <div className='service-period'>
          <p className='text'>Escolha o melhor período para o atendimento</p>
          {SCHEDULE_SHIFTS[scheduleType]()}
        </div>
      )
    )
  }

  if (isLoading) return <ContextLoader fixed={false} />

  return (
    <div className='rescheduling-so'>
      <div className='content'>
        <h3 className='title'>{'Reagendamento da\n instalação'}</h3>
        <div className='section'>
          <p className='text'>Sua instalação está agendada para:</p>
          <p className='text bold'>{`${formatDate(expectedInstallationDate)} Turno: ${formatTurn(
            expectedInstallationTurn
          )}`}</p>
        </div>
        <div className='section'>
          <p className='text'>Deseja alterar o dia da sua instalação?</p>
          <p className='text margin'>Selecione abaixo uma nova data:</p>
        </div>
        <CustomDatePicker
          name='calender-sheduling'
          availableDates={mapBookingAvailableDays()}
          onChange={handleChangeSchedule}
          startDate={new Date()}
          selectedDate={selectedDate}
          dataCy='select-schedule-day'
          placeholderText={formatDate(expectedInstallationDate)}
        />
        {renderScheduleShifts()}
        <div className='section left'>
          <p className='text'>Informe um ponto de referência do endereço</p>
        </div>
        <Input
          name='referencepoint'
          required='true'
          label='Ponto de referência*'
          onChange={event => setReference(event.target.value)}
          value={reference}
        />
      </div>
      {renderButton()}
    </div>
  )
}
